import React, { useState } from 'react';

import Button from 'components/button/Button';
import DropdownContainer from 'components/input/DropdownContainer';
import InputContainer from 'components/input/InputContainer';
import SearchableDropdown from 'components/input/SearchableDropdown';
import ContentModalHeader from 'components/modal/ContentModalHeader';
import { useTranslation } from 'react-i18next';
import { useI18nOptions } from 'shared/hooks/useI18nOptions';
import { removeWhitespace } from 'shared/lib';
import { BirthdaySelect } from 'shared/ui';

import { findCountryCodeOption, trackSignupFieldError } from '../../lib';
import SignupBenefitLine from './SignupBenefitLine';

type SignupStep3Props = {
  renderForm?: (fields: React.ReactNode, validateDetails: () => boolean) => React.ReactNode;
  form: {
    gender: string;
    birthday: string;
    countryCode: string;
    phoneNumber: string;
    nationality: string;
    occupation: string;
    visaInfo: string;
    preferredLanguageCode: string;
  };
  onInputChange: (field: string, value: string) => void;
  onClickNext?: () => void;
};

const SignupStep3 = ({ form, onInputChange, onClickNext, renderForm }: SignupStep3Props) => {
  const { t } = useTranslation('auth');
  const {
    genderOptions,
    occupationOptions,
    visaInfoOptions,
    preferredLanguageOptions,
    countryCodeOptions,
    nationalityOptions,
    getOriginalGenderByIndex,
    getOriginalOccupationByIndex,
    getOriginalVisaInfoByIndex,
    getOriginalPreferredLanguageByIndex,
  } = useI18nOptions();
  const [genderError, setGenderError] = useState('');
  const [birthdayError, setBirthdayError] = useState('');
  const [countryCodeError, setCountryCodeError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [nationalityError, setNationalityError] = useState('');
  const [occupationError, setOccupationError] = useState('');
  const [visaInfoError, setVisaInfoError] = useState('');
  const [preferredLanguageError, setPreferredLanguageError] = useState('');

  const onChangePhoneNumber = (field: string, value: string) => {
    setPhoneNumberError('');
    onInputChange(field, removeWhitespace(value, 'NUM'));
  };

  const validateDetails = () => {
    if (form.gender === '') {
      setGenderError(t('signup.step3.selectGenderError'));
      trackSignupFieldError(3, 'gender');
      return false;
    }

    if (form.birthday === '') {
      setBirthdayError(t('signup.step3.selectBirthdayError'));
      trackSignupFieldError(3, 'birthday');
      return false;
    }

    if (form.nationality === '') {
      setNationalityError(t('signup.step3.enterNationalityError'));
      trackSignupFieldError(3, 'nationality');
      return false;
    }

    if (form.countryCode === '') {
      setCountryCodeError(t('signup.step3.selectCountryCodeError'));
      trackSignupFieldError(3, 'country_code');
      return false;
    }

    if (form.phoneNumber === '') {
      setPhoneNumberError(t('signup.step3.enterPhoneNumberError'));
      trackSignupFieldError(3, 'phone_number');
      return false;
    }
    onInputChange('phoneNumber', removeWhitespace(form.phoneNumber, 'NUM'));

    if (form.occupation === '') {
      setOccupationError(t('signup.step3.enterOccupationError'));
      trackSignupFieldError(3, 'occupation');
      return false;
    }

    if (form.visaInfo === '') {
      setVisaInfoError(t('signup.step3.enterVisaInfoError'));
      trackSignupFieldError(3, 'visa');
      return false;
    }

    if (form.preferredLanguageCode === '') {
      setPreferredLanguageError(t('signup.step3.preferredLanguageError'));
      trackSignupFieldError(3, 'preferred_language');
      return false;
    }

    return true;
  };

  const handleNext = () => {
    if (!validateDetails()) return;
    window.dataLayer?.push({
      event: 'sign_up_step3',
    });

    onClickNext?.();
  };

  const fields = (
    <div
      className={
        renderForm ? 'grid w-full grid-cols-1 gap-x-4 gap-y-1 md:grid-cols-2' : 'flex w-full flex-col gap-y-px'
      }
    >
      <DropdownContainer
        className="flex-1"
        label={t('signup.step3.biologicalSexLabel')}
        placeholder={t('signup.step3.select')}
        value={form.gender}
        setValue={(value) => {
          setGenderError('');
          const index = genderOptions.indexOf(value);
          const originalValue = getOriginalGenderByIndex(index);
          onInputChange('gender', originalValue);
        }}
        options={genderOptions}
        error={genderError}
      />
      <div className="flex w-full flex-col gap-y-0.5">
        <div className="flex w-full flex-col gap-y-2">
          <span className="text-c3">{t('signup.step3.dateOfBirthLabel')}</span>
          <BirthdaySelect
            value={form.birthday}
            onChange={(value) => {
              setBirthdayError('');
              onInputChange('birthday', value);
            }}
            isError={birthdayError !== ''}
          />
        </div>
        <span className="h-4 text-c4 text-error">{birthdayError}</span>
      </div>
      <SearchableDropdown
        label={t('signup.step3.nationalityLabel')}
        placeholder={t('signup.step3.select')}
        searchPlaceholder={t('signup.step3.selectSearch')}
        value={form.nationality}
        setValue={(value) => {
          setNationalityError('');
          // 국가번호가 비어 있거나 이전 국적으로 자동 선택된 값이면 새 국적에 맞춰 바꾼다
          const isAutoCountryCode =
            form.countryCode === '' || form.countryCode === findCountryCodeOption(form.nationality);
          const countryCodeOption = findCountryCodeOption(value);
          if (isAutoCountryCode && countryCodeOption) {
            setCountryCodeError('');
            onInputChange('countryCode', countryCodeOption);
          }
          onInputChange('nationality', value);
        }}
        // TODO: 추후 수정
        options={nationalityOptions as unknown as string[]}
        error={nationalityError}
      />
      <div className="flex w-full gap-x-2">
        <DropdownContainer
          className={renderForm ? 'w-[112px] shrink-0' : 'w-[140px] shrink-0'}
          label={t('signup.step3.countryCodeLabel')}
          placeholder={t('signup.step3.select')}
          value={renderForm && form.countryCode ? `+${removeWhitespace(form.countryCode, 'NUM')}` : form.countryCode}
          setValue={(value) => {
            setCountryCodeError('');
            onInputChange('countryCode', value);
          }}
          options={countryCodeOptions}
          error={countryCodeError}
        />
        <InputContainer
          className="min-w-0 flex-1"
          placeholder={t('signup.step3.phoneNumberPlaceholder')}
          label={t('signup.step3.phoneNumberLabel')}
          value={form.phoneNumber}
          onChange={(e) => onChangePhoneNumber('phoneNumber', e.target.value)}
          error={phoneNumberError}
        />
      </div>
      <DropdownContainer
        label={t('signup.step3.occupationLabel')}
        placeholder={t('signup.step3.select')}
        value={form.occupation}
        setValue={(value) => {
          setOccupationError('');
          const index = occupationOptions.indexOf(value);
          const originalValue = getOriginalOccupationByIndex(index);
          onInputChange('occupation', originalValue);
        }}
        options={occupationOptions}
        error={occupationError}
      />
      <DropdownContainer
        label={t('signup.step3.visaInfoLabel')}
        placeholder={t('signup.step3.select')}
        value={form.visaInfo}
        setValue={(value) => {
          setVisaInfoError('');
          const index = visaInfoOptions.indexOf(value);
          const originalValue = getOriginalVisaInfoByIndex(index);
          onInputChange('visaInfo', originalValue);
        }}
        options={visaInfoOptions}
        error={visaInfoError}
      />
      <DropdownContainer
        label={t('signup.step3.preferredLanguageLabel')}
        placeholder={t('signup.step3.select')}
        value={(() => {
          const index = preferredLanguageOptions.findIndex(
            (_, i) => getOriginalPreferredLanguageByIndex(i) === form.preferredLanguageCode,
          );
          return index !== -1 ? preferredLanguageOptions[index] : '';
        })()}
        setValue={(value) => {
          setPreferredLanguageError('');
          const index = preferredLanguageOptions.indexOf(value);
          const originalValue = getOriginalPreferredLanguageByIndex(index);
          onInputChange('preferredLanguageCode', originalValue);
        }}
        options={preferredLanguageOptions}
        error={preferredLanguageError}
      />
    </div>
  );

  if (renderForm) return <>{renderForm(fields, validateDetails)}</>;

  return (
    <div className="flex max-h-[calc(100vh-160px)] w-[calc(100vw-32px)] max-w-[408px] flex-col gap-y-5 overflow-y-auto p-5 pb-6 md:mx-30 md:mb-8 md:mt-6 md:max-h-none md:w-[408px] md:gap-y-7 md:overflow-visible md:p-0">
      <div className="flex w-full flex-col gap-y-6">
        <ContentModalHeader
          src="/images/optimized/illusts/il_mail.webp"
          title={t('signup.title')}
          description={t('signup.step3.description')}
        />
        <SignupBenefitLine className="-mt-2" />
        {fields}
      </div>
      <Button text={t('signup.step3.continue')} shape="SQUARE" onClick={handleNext} />
    </div>
  );
};

export default SignupStep3;
